export type BlockFont = { family: string | null; size: string | null }

export const INLINE_TEXT_BLOCK_SELECTOR =
  'p, li, td, th, h1, h2, h3, h4, h5, h6'

const BLOCK_LEVEL_TAGS = new Set([
  'address',
  'article',
  'blockquote',
  'div',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
])

export const isBlockLevelTag = (tagName: string): boolean =>
  BLOCK_LEVEL_TAGS.has(tagName.toLowerCase())

export const holdsInlineText = (text: string): boolean =>
  /[^\s]|\u00a0/.test(text)

export const isHiddenByStyle = (style: string | null | undefined): boolean =>
  /display\s*:\s*none/i.test(style ?? '')

const declarationPattern = (property: string): RegExp =>
  new RegExp(`(^|;)(\\s*)${property}\\s*:\\s*([^;]*)(;?)`, 'i')

export const fontDeclarationsOf = (
  style: string | null | undefined,
): BlockFont => ({
  family: style?.match(declarationPattern('font-family'))?.[3]?.trim() || null,
  size: style?.match(declarationPattern('font-size'))?.[3]?.trim() || null,
})

const PX_PER_UNIT: Record<string, number> = {
  px: 1,
  pt: 96 / 72,
  pc: 16,
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
}

export const fontSizePxOf = (size: string | null): number | null => {
  if (!size) return null
  const match = size.trim().match(/^([\d.]+)\s*([a-z%]*)$/i)
  if (!match) return null
  const value = Number.parseFloat(match[1])
  const perUnit = PX_PER_UNIT[match[2].toLowerCase() || 'px']
  return Number.isFinite(value) && perUnit !== undefined
    ? value * perUnit
    : null
}

/**
 * The font a block's strut takes: the family and size of its tallest sized
 * run, so every line of the block is at least that run's line box and no
 * line is the union of unlike boxes; the earliest of equals wins, and a
 * family without a size beside it comes from the first run naming one.
 */
export const tallestRunFont = (runs: readonly BlockFont[]): BlockFont => {
  let tallest: BlockFont | null = null
  let tallestPx = -1
  let firstFamily: string | null = null
  for (const run of runs) {
    firstFamily ??= run.family
    const px = fontSizePxOf(run.size)
    if (px === null || px <= tallestPx) continue
    tallestPx = px
    tallest = run
  }
  return {
    family: tallest?.family ?? firstFamily,
    size: tallest?.size ?? null,
  }
}

const withDeclaration = (
  style: string,
  property: string,
  value: string,
): string => {
  const pattern = declarationPattern(property)
  if (pattern.test(style))
    return style.replace(
      pattern,
      (_match, lead: string, space: string, _value: string, semi: string) =>
        `${lead}${space}${property}: ${value}${semi || ';'}`,
    )
  const trimmed = style.trim()
  const glue = trimmed === '' ? '' : trimmed.endsWith(';') ? ' ' : '; '
  return `${trimmed}${glue}${property}: ${value};`
}

export const withBlockFont = (style: string, font: BlockFont): string => {
  let next = style
  if (font.family) next = withDeclaration(next, 'font-family', font.family)
  if (font.size) next = withDeclaration(next, 'font-size', font.size)
  return next
}

const hasFont = (font: BlockFont): boolean =>
  font.family !== null || font.size !== null

/**
 * The font to write on each block: its own text's, or nothing. A block whose
 * text carries no font of its own is left for CSS to settle — its words and
 * its strut both take what the block sits inside, so the two match without
 * being written down, and a font from the paragraph beside it would override
 * whatever declared them. Every such block in the dev9 corpus sits in a
 * `mceNonEditable` template div carrying the face and size the design chose.
 */
export const blockFontsToPin = (
  own: readonly BlockFont[],
): Array<BlockFont | null> => own.map((font) => (hasFont(font) ? font : null))
